// The Filters dialog's categories for the Bids/Offers and Trades boards (charts/boardFilters.js).
// A port of the ECI mobile app's src/boardOptions.ts (BO_FILTERS, TRADE_FILTERS). Keep the two
// in step. No Angular here, so Node can test it (boardFilters.test.mjs).
import {compareRegions} from './regionFlows.js';
import {isFormulaPriced} from './marketToday.js';

const trimmed = s => String(s ?? '').trim();
const one = (value, label = trimmed(value)) => (trimmed(value) ? [{key: trimmed(value), label}] : []);
const byRegion = (a, b) => compareRegions(a.key, b.key);

/**
 * A row's specifications as filter values: a yes/no one by its name ("SQF Certified"), a chosen
 * one by name and value ("Quality: AA", each of a multiselect's), CA compliance once (it is a
 * column and, on newer rows, an attribute too). Dates and other typed-in values aren't offered.
 */
function attributeValues(row) {
  const values = Number(row.CACompliant) === 1 ? [{key: 'ca', label: 'CA Compliant'}] : [];
  Object.entries(row.attributes || {}).forEach(([name, attr]) => {
    const title = attr.title || name;
    if (name === 'ca_compliant') {
      return;
    }
    if (attr.type === 'boolean') {
      if (attr.value === true) {
        values.push({key: name, label: title});
      }
    } else if (attr.type === 'select') {
      if (trimmed(attr.value)) {
        values.push({key: `${name}=${trimmed(attr.value)}`, label: `${title}: ${trimmed(attr.value)}`});
      }
    } else if (attr.type === 'multiselect' && Array.isArray(attr.value)) {
      attr.value.forEach(v => values.push({key: `${name}=${trimmed(v)}`, label: `${title}: ${trimmed(v)}`}));
    }
  });
  return values;
}

// Shared by both boards, in the dialog's order.
const productCategories = () => [
  {key: 'eggType', title: 'Egg Type', values: r => one(r.EggType)},
  {key: 'color', title: 'Color', values: r => one(r.EggColor)},
  {key: 'class', title: 'Class', values: r => one(r.EggClass)},
  {key: 'pack', title: 'Pack', values: r => one(r.EggPack)},
];
const termsCategories = () => [
  {key: 'delivery', title: 'Delivery or Pickup',
    values: r => [Number(r.PickUp) === 1 ? {key: 'pickup', label: 'Pickup'} : {key: 'delivery', label: 'Delivery'}],
    order: (a, b) => (a.key === 'delivery' ? -1 : b.key === 'delivery' ? 1 : 0)},
  {key: 'pricing', title: 'Pricing',
    values: r => [isFormulaPriced(r) ? {key: 'market', label: 'Market-based'} : {key: 'flat', label: 'Flat price'}],
    order: (a, b) => (a.key === 'flat' ? -1 : b.key === 'flat' ? 1 : 0)},
  {key: 'attributes', title: 'Specifications', values: attributeValues},
];

export const BO_FILTERS = [
  {key: 'side', title: 'Bids or Offers',
    values: b => [String(b.BO).trim() === 'B' ? {key: 'B', label: 'Bids'} : {key: 'O', label: 'Offers'}],
    order: (a, b) => a.key.localeCompare(b.key)},
  ...productCategories(),
  {key: 'region', title: 'Region', values: b => one(b.Region), order: byRegion},
  ...termsCategories(),
];

export const TRADE_FILTERS = [
  ...productCategories(),
  {key: 'buyerRegion', title: 'Buyer Region', values: t => one(t.BuyerRegion), order: byRegion},
  {key: 'sellerRegion', title: 'Seller Region', values: t => one(t.SellerRegion), order: byRegion},
  ...termsCategories(),
];
